import React, { useState, useRef, useEffect } from "react";
import { Box, IconButton, Typography, Slider, Tooltip } from "@mui/material";
import { PlayArrow, Pause, Delete } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";

const formatTime = (seconds) => {
  if (seconds === undefined || Number.isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * Pré-visualização do áudio gravado antes do envio.
 * @param {boolean} embedded — dentro da cápsula do canal (substitui só o TextField).
 */
const AudioPreview = ({ audioURL, onRemove, embedded = false }) => {
  const { t } = useTranslation("chat");
  const theme = useTheme();
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!audioURL || !audioRef.current) return;
    const el = audioRef.current;
    const onLoadedMetadata = () => setDuration(el.duration);
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
    };
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);
    if (el.duration && !Number.isNaN(el.duration)) setDuration(el.duration);
    return () => {
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioURL]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleSeek = (_, value) => {
    if (!audioRef.current || duration <= 0) return;
    const t0 = Array.isArray(value) ? value[0] : value;
    const sec = (t0 / 100) * duration;
    audioRef.current.currentTime = sec;
    setCurrentTime(sec);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!audioURL) return null;

  const barTitle = `${t("input.audioPreviewBadge")} — ${formatTime(currentTime)} / ${formatTime(duration)}`;

  const rootSx = embedded
    ? {
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 0.75 },
        flex: 1,
        alignSelf: "stretch",
        minWidth: 0,
        minHeight: `${COMPOSER_ROW_HEIGHT_PX}px`,
        boxSizing: "border-box",
        py: 0,
        px: { xs: 0.25, sm: 0.5 },
        borderRadius: 0,
        border: "none",
        overflow: "hidden",
        width: "100%",
        backgroundColor: "transparent",
        boxShadow: "none",
      }
    : {
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.5, sm: 0.75 },
        height: COMPOSER_ROW_HEIGHT_PX,
        minHeight: COMPOSER_ROW_HEIGHT_PX,
        maxHeight: COMPOSER_ROW_HEIGHT_PX,
        maxWidth: "100%",
        boxSizing: "border-box",
        py: 0,
        px: { xs: 0.75, sm: 1 },
        borderRadius: `${COMPOSER_ROW_HEIGHT_PX / 2}px`,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        width: "100%",
        boxShadow:
          theme.palette.mode === "dark"
            ? `0 1px 0 ${alpha(theme.palette.common.black, 0.35)} inset`
            : "0 1px 2px rgba(15, 23, 42, 0.05), 0 3px 12px rgba(15, 23, 42, 0.05)",
      };

  const inner = (
    <>
      <audio ref={audioRef} src={audioURL} preload="metadata" />
      <Tooltip title={playing ? t("input.audioPreviewPause") : t("input.audioPreviewPlay")}>
        <IconButton
          size="small"
          onClick={togglePlay}
          sx={{
            color: "primary.main",
            flexShrink: 0,
            alignSelf: "center",
            backgroundColor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.2 : 0.12),
            padding: "6px",
            "&:hover": {
              backgroundColor: (th) => alpha(th.palette.primary.main, th.palette.mode === "dark" ? 0.3 : 0.2),
            },
          }}
          aria-label={playing ? t("input.audioPreviewPause") : t("input.audioPreviewPlay")}
        >
          {playing ? <Pause sx={{ fontSize: 24 }} /> : <PlayArrow sx={{ fontSize: 24 }} />}
        </IconButton>
      </Tooltip>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.15,
          alignSelf: "stretch",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="body2"
          component="div"
          noWrap
          sx={{
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            color: "text.primary",
            fontSize: "0.75rem",
            lineHeight: 1.15,
          }}
        >
          <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
            {formatTime(currentTime)}
          </Box>
          <Box component="span" sx={{ color: "text.disabled", fontWeight: 500, mx: 0.35 }}>
            /
          </Box>
          <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
            {formatTime(duration)}
          </Box>
        </Typography>

        <Box
          sx={{
            width: "100%",
            height: 18,
            display: "flex",
            alignItems: "center",
            position: "relative",
            flexShrink: 0,
          }}
        >
          <Slider
            size="small"
            value={progress}
            onChange={handleSeek}
            sx={{
              width: "100%",
              m: 0,
              p: 0,
              height: 18,
              color: "primary.main",
              "& .MuiSlider-rail": {
                opacity: 0.35,
                height: 4,
                borderRadius: 2,
              },
              "& .MuiSlider-track": {
                height: 4,
                borderRadius: 2,
              },
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                boxShadow: "0 0 0 2px rgba(255,255,255,0.95)",
                "&:hover, &.Mui-focusVisible": {
                  boxShadow: "0 0 0 6px rgba(25,118,210,0.16)",
                },
              },
            }}
            aria-label={t("input.audioSeekAria")}
          />
        </Box>
      </Box>

      <Tooltip title={t("input.audioDeleteTooltip")}>
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            color: "text.secondary",
            flexShrink: 0,
            alignSelf: "center",
            padding: "6px",
            "&:hover": { color: "error.main", backgroundColor: "action.hover" },
          }}
          aria-label={t("input.audioDeleteTooltip")}
        >
          <Delete sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    </>
  );

  if (embedded) {
    return (
      <Box component="section" aria-label={t("input.audioPreviewBadge")} sx={rootSx}>
        {inner}
      </Box>
    );
  }

  return (
    <Tooltip title={barTitle} enterDelay={500}>
      <Box component="section" aria-label={t("input.audioPreviewBadge")} sx={rootSx}>
        {inner}
      </Box>
    </Tooltip>
  );
};

export default AudioPreview;
